// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The model registry: which tokenizer counts a model, and what limit its rows
 * are measured against.
 *
 * THE LIMIT IS A SEPARATE NUMBER FROM THE CONTEXT WINDOW, and confusing them is
 * the defect this tool exists to avoid. OpenAI's fine-tuning guide publishes a
 * table whose "maximum examples context length" is 65,536 tokens for the
 * gpt-4.1 and gpt-4o families, against a 128,000-token inference window —
 * exactly half. A dataset checked against the context window passes and then
 * loses the answer from every long row.
 *
 * Every limit here is `trainingLimit`, never a context window, and each carries
 * the source it came from. Where a provider does not publish one, it is `null`
 * and the interface asks the user for it rather than inventing a number.
 */

export type Backend = 'openai' | 'hf';

export interface ModelEntry {
  readonly id: string;
  readonly label: string;
  readonly backend: Backend;
  /**
   * For `openai`: the gpt-tokenizer encoding subpath name.
   * For `hf`: the Hugging Face repo whose `tokenizer.json` is fetched.
   */
  readonly tokenizer: string;
  /** Maximum tokens in ONE TRAINING EXAMPLE. Not the inference context window. */
  readonly trainingLimit: number | null;
  /** The inference context window, shown only to make the difference visible. */
  readonly contextWindow: number | null;
  readonly source: string;
  /** Approximate bytes of `tokenizer.json`, so the size is shown before the download. */
  readonly downloadBytes?: number;
  readonly note?: string;
}

/**
 * The date every claim on this page was checked. Shown in the interface, because
 * a price or a limit with no date on it is a number nobody can audit.
 */
export const TABLE_CHECKED = '2026-08-17';

export const MODELS: readonly ModelEntry[] = [
  /* ── OpenAI encodings: bundled, no network, exact ids ──────────────────── */
  {
    id: 'gpt-4.1',
    label: 'GPT-4.1 / 4.1-mini / 4.1-nano',
    backend: 'openai',
    tokenizer: 'o200k_base',
    trainingLimit: 65_536,
    contextWindow: 128_000,
    source: 'OpenAI fine-tuning best practices, Token limits table',
  },
  {
    id: 'gpt-4o',
    label: 'GPT-4o / 4o-mini',
    backend: 'openai',
    tokenizer: 'o200k_base',
    trainingLimit: 65_536,
    contextWindow: 128_000,
    source: 'OpenAI fine-tuning best practices, Token limits table',
  },
  {
    id: 'gpt-3.5-turbo',
    label: 'GPT-3.5 Turbo (legacy)',
    backend: 'openai',
    tokenizer: 'cl100k_base',
    trainingLimit: 16_385,
    contextWindow: 16_385,
    source: 'OpenAI fine-tuning docs, gpt-3.5-turbo-0125',
    note: 'The one model whose training limit and context window are the same number.',
  },
  {
    id: 'gpt-oss',
    label: 'gpt-oss (Harmony)',
    backend: 'openai',
    tokenizer: 'o200k_harmony',
    trainingLimit: null,
    contextWindow: 131_072,
    source: 'Set your own limit — depends on who you train it with',
  },
  {
    id: 'cl100k',
    label: 'Older OpenAI / embeddings (cl100k_base)',
    backend: 'openai',
    tokenizer: 'cl100k_base',
    trainingLimit: null,
    contextWindow: 8_191,
    source: 'text-embedding-3-*, GPT-4 (legacy), GPT-3.5',
  },
  {
    id: 'p50k',
    label: 'Legacy completions (p50k_base)',
    backend: 'openai',
    tokenizer: 'p50k_base',
    trainingLimit: null,
    contextWindow: 4_097,
    source: 'text-davinci-003 era',
    note: 'Fine-tuning on davinci-002 and babbage-002 was shut down on 2024-10-28. Kept for counting old files.',
  },

  /* ── Open models: tokenizer.json fetched from Hugging Face ─────────────── */
  {
    id: 'qwen2.5',
    label: 'Qwen2.5 (7B Instruct)',
    backend: 'hf',
    tokenizer: 'Qwen/Qwen2.5-7B-Instruct',
    trainingLimit: 32_768,
    contextWindow: 131_072,
    source: 'Fireworks caps training examples at 32,768 tokens',
    downloadBytes: 7_031_645,
  },
  {
    id: 'qwen3',
    label: 'Qwen3 (8B)',
    backend: 'hf',
    tokenizer: 'Qwen/Qwen3-8B',
    trainingLimit: 32_768,
    contextWindow: 131_072,
    source: 'Fireworks caps training examples at 32,768 tokens',
    downloadBytes: 11_422_654,
  },
  {
    id: 'mistral',
    label: 'Mistral 7B Instruct v0.3',
    backend: 'hf',
    tokenizer: 'mistralai/Mistral-7B-Instruct-v0.3',
    trainingLimit: 32_768,
    contextWindow: 32_768,
    source: 'Fireworks caps training examples at 32,768 tokens',
    downloadBytes: 1_961_548,
    note: 'A SentencePiece-style tokenizer. Measurably slower than the others — the estimate below is real.',
  },
  {
    id: 'deepseek',
    label: 'DeepSeek V3',
    backend: 'hf',
    tokenizer: 'deepseek-ai/DeepSeek-V3',
    trainingLimit: null,
    contextWindow: 131_072,
    source: 'Set your own limit',
    downloadBytes: 7_847_652,
  },
  {
    id: 'llama3.1',
    label: 'Llama 3.1 8B Instruct',
    backend: 'hf',
    // meta-llama/* returns HTTP 401 to an anonymous request — measured 2026-08-17
    // with an Origin header — so it cannot be fetched from a page with no account
    // behind it. This is the unsloth mirror, which is ungated. It is named in the
    // interface rather than silently substituted.
    tokenizer: 'unsloth/Meta-Llama-3.1-8B-Instruct',
    trainingLimit: null,
    contextWindow: 131_072,
    source: 'Set your own limit',
    downloadBytes: 17_209_920,
    note: 'Fetched from the ungated unsloth mirror: Meta’s own repository requires a signed-in account.',
  },
];

/**
 * Models deliberately absent, and why. Shown in the interface — a missing option
 * with no explanation reads as an oversight, and two of these are ones people
 * will actively look for.
 */
export const EXCLUDED: readonly { readonly label: string; readonly reason: string }[] = [
  {
    label: 'Claude (any version)',
    reason:
      'There is no public local tokenizer for Claude after Claude 3, and Claude now uses different tokenizers for different models, so a single ratio would be wrong in a new way for each one. Anthropic offers an exact count through its own /v1/messages/count_tokens endpoint, which is an authenticated network call and therefore out of scope for a tool that sends nothing.',
  },
  {
    label: 'Gemini',
    reason:
      'Google publishes no local tokenizer. countTokens is a server call against your own API key.',
  },
  {
    label: 'o4-mini (reinforcement fine-tuning)',
    reason:
      'Billed at $100 per HOUR rather than per token, so a token count cannot produce a cost for it. Pricing it per token would be a fabricated number.',
  },
  {
    label: 'davinci-002 and babbage-002',
    reason: 'Fine-tuning training on both was shut down on 2024-10-28.',
  },
];

export function modelById(id: string): ModelEntry {
  const found = MODELS.find((m) => m.id === id);
  if (!found) throw new Error(`unknown model ${id}`);
  return found;
}

/**
 * Known-good token ids, asserted before the first row is counted.
 *
 * This exists because of a real, live defect: gpt-tokenizer 3.4.0 shipped a
 * `dist/cl100k_base.js` that is a MISLABELLED COPY of o200k_base. Loaded in
 * isolation it reports vocabularySize 200006 and encodes "hello world" as
 * [24912, 2375] — o200k's answer — where cl100k's is [15339, 1917]. The file is
 * 2,048,099 bytes against real cl100k ranks of about 1.16 MB, and the version on
 * unpkg is byte-identical, so anything built on it was silently counting every
 * GPT-4, GPT-3.5 and embedding file with the wrong vocabulary and no error.
 *
 * A count that is wrong is worse than a count that fails, so the tool refuses to
 * start on an encoding whose ids do not match. Pinning two ids costs nothing and
 * makes that class of bug unshippable.
 */
export const ENCODING_SELF_CHECK: Readonly<Record<string, { readonly text: string; readonly ids: readonly number[]; readonly vocab: number }>> = {
  cl100k_base: { text: 'hello world', ids: [15339, 1917], vocab: 100_264 },
  o200k_base: { text: 'hello world', ids: [24912, 2375], vocab: 200_006 },
  o200k_harmony: { text: 'hello world', ids: [24912, 2375], vocab: 201_089 },
  p50k_base: { text: 'hello world', ids: [31373, 995], vocab: 50_281 },
};
